import CryptoJS from 'crypto-js';

type WordArray = CryptoJS.lib.WordArray;

export class Convert {
    /**
     * @see https://gist.github.com/artjomb/7ef1ee574a411ba0dd1933c1ef4690d1
     *
     * @param byteArray The byteArray (Uint8Array) to be converted into a WordArray
     *
     * @return Returns a WordArray to be used in the CryptoJS library
     */
    public static uint8ArrayToWordArray(byteArray: Uint8Array) {
        const wordArray: number[] = [];

        for (let i = 0; i < byteArray.length; i += 1) {
            wordArray[(i / 4) | 0] |= byteArray[i] << (24 - 8 * i);
        }

        return CryptoJS.lib.WordArray.create(wordArray, byteArray.length);
    }

    /**
     * @see https://gist.github.com/artjomb/7ef1ee574a411ba0dd1933c1ef4690d1
     *
     * @param wordArray The wordArray (WordArray) to be converted into a byte array (Uint8Array)
     *
     * @return Returns a byte array (Uint8Array) to be used across the application
     */
    public static wordArrayToUint8Array(wordArray: WordArray) {
        const result = [];
        let bytes;
        let i = 0;

        while (wordArray.sigBytes > 0) {
            bytes = Convert.wordToByteArray(
                wordArray.words[i],
                Math.min(4, wordArray.sigBytes)
            );
            wordArray.sigBytes -= bytes.length;
            result.push(bytes);

            i += 1;
        }

        return new Uint8Array(result.flat());
    }

    /**
     * @see https://gist.github.com/artjomb/7ef1ee574a411ba0dd1933c1ef4690d1
     *
     * @param word
     * @param length
     *
     * @return
     */
    private static wordToByteArray(word: number, length: number): number[] {
        const byteArray = [];
        const xFF = 0xff;

        if (length > 0) byteArray.push(word >>> 24);
        if (length > 1) byteArray.push((word >>> 16) & xFF);
        if (length > 2) byteArray.push((word >>> 8) & xFF);
        if (length > 3) byteArray.push(word & xFF);

        return byteArray;
    }

    /**
     * Converts a hex string into Uint8Array
     *
     * @param hexString The hex string to be converted
     *
     * @return Returns a byte array (Uint8Array) to be used across the application
     */
    public static hexStringToUint8Array(hexString: string) {
        if (hexString.length % 2 !== 0) {
            throw new Error('Invalid hexString');
        }

        const byteArray = new Uint8Array(hexString.length / 2);

        for (let i = 0, l = hexString.length; i < l; i += 2) {
            var byteValue = parseInt(hexString.substr(i, 2), 16);

            if (isNaN(byteValue)) {
                throw new Error('Invalid hexString');
            }

            byteArray[i / 2] = byteValue;
        }

        return byteArray;
    }

    /**
     * Converts an Uint8Array to hex string
     *
     * @param byteArray The byteArray (Uint8Array) to be converted
     *
     * @return Returns a hex string to be used across the application
     */
    public static uint8ArrayToHexString(byteArray: Uint8Array) {
        let hexString = '';
        let nextHexByte;

        for (var i = 0, l = byteArray.byteLength; i < l; i += 1) {
            nextHexByte = byteArray[i].toString(16); // Integer to base 16

            if (nextHexByte.length < 2) {
                nextHexByte = '0' + nextHexByte; // Otherwise 10 becomes just a instead of 0a
            }

            hexString += nextHexByte;
        }

        return hexString;
    }
}
